import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Alert, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import './calls.css';

const MUTED = 'var(--rr-color-text-muted)';

/** A body as it is easiest to read: JSON laid out, anything else as it came. */
function pretty(body?: string | null): string {
  if (!body) return '';
  const trimmed = body.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try { return JSON.stringify(JSON.parse(trimmed), null, 2); } catch { /* not JSON after all */ }
  }
  return body;
}

function Side({ side, testId }: { side: flows.HttpCallSide; testId: string }) {
  const t = useT();
  const body = pretty(side.body);
  const copy = async () => {
    try { await navigator.clipboard.writeText(body); notifySuccess(t('flows.calls.copied')); } catch { /* the browser refused: nothing to do */ }
  };
  return (
    <Stack gap="xs" data-testid={testId}>
      <Text role="inputLabel">{t('flows.calls.headers')}</Text>
      {side.headers.length === 0 ? (
        <Text role="caption" c={MUTED}>{t('flows.calls.noHeaders')}</Text>
      ) : (
        <table className="rr-calls__headers">
          <tbody>
            {side.headers.map(([name, value], i) => (
              <tr key={`${name}-${i}`}><th>{name}</th><td>{value}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      <Group justify="space-between">
        <Text role="inputLabel">{t('flows.calls.body')}</Text>
        {body && <Button size="xs" variant="subtle" onClick={() => void copy()}>{t('flows.calls.copy')}</Button>}
      </Group>
      {body ? <pre className="rr-calls__body">{body}</pre> : <Text role="caption" c={MUTED}>{t('flows.calls.noBody')}</Text>}
    </Stack>
  );
}

/**
 * One API call a flow made (19 October 2026): what was sent and what came back, with every key and password hidden.
 * Kept 48 hours; after that the drawer says so.
 */
export function HttpCallDrawer({ callId, onClose }: { callId: string | null; onClose: () => void }) {
  const t = useT();
  const [tab, setTab] = useState<string>('response');
  const detail = useQuery({
    queryKey: ['http-log', callId],
    queryFn: () => flows.getHttpLog(callId!),
    enabled: callId !== null,
    retry: false
  });
  const call = detail.data?.data.call;
  const gone = detail.error && (detail.error as { status?: number }).status === 404;

  return (
    <Drawer opened={callId !== null} onClose={onClose} position="right" size="lg" title={t('flows.calls.drawerTitle')}>
      <Stack gap="sm" style={{ padding: 4 }} data-testid="http-call-drawer">
        <Text role="caption" c={MUTED}>{t('flows.calls.kept')}</Text>
        {gone && <Alert color="info" role="status">{t('flows.calls.gone')}</Alert>}
        {detail.error && !gone && <Alert color="error" role="alert">{(detail.error as Error).message}</Alert>}
        {detail.isPending && callId !== null && <Text role="caption" c={MUTED}>{t('common.loading')}</Text>}
        {call && detail.data && (
          <>
            <Group gap="xs" wrap="wrap">
              <Badge color={call.ok ? 'green' : 'red'} variant="light">{call.status === 0 ? t('flows.calls.noAnswer') : call.status}</Badge>
              <Text role="bodySmall" fw={650}>{call.method}</Text>
              {call.from_cache && <Badge variant="light">{t('flows.calls.fromCache')}</Badge>}
              <Text role="caption" c={MUTED}>{t('flows.calls.took', { ms: call.duration_ms })}</Text>
            </Group>
            <Text role="bodySmall" className="rr-calls__url" data-testid="http-call-url">{call.url}</Text>
            <Text role="caption" c={MUTED}>
              {[call.flow_name, call.node_name || call.node_id, formatDateTime(call.created_at)].filter(Boolean).join(' · ')}
            </Text>
            {call.error && <Alert color="error" role="alert">{call.error}</Alert>}
            <Tabs value={tab} onChange={(v) => setTab(v ?? 'response')}>
              <Tabs.List>
                <Tabs.Tab value="request">{t('flows.calls.request')}</Tabs.Tab>
                <Tabs.Tab value="response">{t('flows.calls.response')}</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="request"><div style={{ paddingTop: 12 }}><Side side={detail.data.data.request} testId="http-call-request" /></div></Tabs.Panel>
              <Tabs.Panel value="response"><div style={{ paddingTop: 12 }}><Side side={detail.data.data.response} testId="http-call-response" /></div></Tabs.Panel>
            </Tabs>
          </>
        )}
      </Stack>
    </Drawer>
  );
}
